import { IButtonStyles } from "@fluentui/react";
import {
  IComboBoxOptionStyles,
  IComboBoxStyles,
} from "@fluentui/react/lib/ComboBox";
import {
  concatStyleSets,
  IStyle,
  mergeStyles,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import { ActionControlSize, controlStylingConstants } from "@1js/acui-common";

import {
  AppComboBoxInternalProps,
  AppComboBoxStyles,
} from "./AppComboBox.Props";
import { getComboBoxContainerMarginStyles } from "./AppComboBox.styles";

export interface ComboBoxSizedStyles {
  styles?: Partial<IComboBoxStyles>;
  optionStyles?: Partial<IComboBoxOptionStyles>;
  caretDownButtonStyles?: IButtonStyles;
  iconStyles?: IStyle;
}

const comboBoxSmallHeightWithIdentifiersAbove = 48;

const getComboBoxSmallStyles = (
  pinToPreviousComboBox?: boolean,
  identifiersAbove?: boolean
): Partial<IComboBoxStyles> => ({
  root: {
    height: controlStylingConstants.comboBoxSmallHeight,
    lineHeight: controlStylingConstants.comboBoxSmallHeight - 2,
    paddingRight: controlStylingConstants.comboBoxSmallCaretWidth,
  },
  container: [
    {
      height: identifiersAbove
        ? comboBoxSmallHeightWithIdentifiersAbove
        : controlStylingConstants.comboBoxSmallHeight,
    },
    getComboBoxContainerMarginStyles(
      controlStylingConstants.comboBoxSmallRightMargin,
      pinToPreviousComboBox
    ),
  ],
  input: {
    fontSize: 12,
    height: 16,
    lineHeight: 16,
  },
  label: {
    fontSize: 12,
    lineHeight: identifiersAbove ? 12 : undefined,
    marginLeft: 4,
    marginRight: 4,
  },
});

export const optionSmallStyles: Partial<IComboBoxOptionStyles> = {
  root: {
    height: 28,
    minHeight: 28,
  },
  optionText: {
    fontSize: 12,
  },
};

export const smallCaretDownButtonStyles: IButtonStyles = {
  root: {
    height: controlStylingConstants.comboBoxSmallHeight,
    width: controlStylingConstants.comboBoxSmallCaretWidth,
    minWidth: controlStylingConstants.comboBoxSmallCaretWidth,
  },
  icon: {
    height: 8,
    width: 8,
    lineHeight: 8,
  },
};

const getComboBoxSmallIconStyles: (
  showLabel?: boolean,
  identifiersAbove?: boolean
) => IStyle = memoizeFunction(
  (showLabel?: boolean, identifiersAbove?: boolean) => {
    return {
      width: 16,
      height: 16,
      marginLeft: identifiersAbove ? 0 : 4,
      marginRight: !showLabel ? 4 : undefined,
      paddingBottom: identifiersAbove ? 5 : undefined,
    };
  }
);

/**
 * Helper for getComboBoxSizedStyles that's memoized on the individual style
 * parameters, rather than the entire props object.
 */
const getComboBoxSizedStylesInternal = memoizeFunction(
  (
    size: ActionControlSize,
    pinToPreviousComboBox?: boolean,
    showLabel?: boolean,
    externalStyles?: Partial<AppComboBoxStyles>,
    externalOptionStyles?: Partial<IComboBoxOptionStyles>,
    externalCaretDownButtonStyles?: IButtonStyles,
    externalIconStyles?: IStyle,
    identifiersAbove?: boolean
  ): ComboBoxSizedStyles => {
    switch (size) {
      case ActionControlSize.Small:
      case ActionControlSize.Large:
        return {
          styles: concatStyleSets(
            getComboBoxSmallStyles(pinToPreviousComboBox, identifiersAbove),
            externalStyles
          ),
          iconStyles: mergeStyles(
            getComboBoxSmallIconStyles(showLabel, identifiersAbove),
            externalIconStyles
          ),
          optionStyles: concatStyleSets(
            optionSmallStyles,
            externalOptionStyles
          ),
          caretDownButtonStyles: concatStyleSets(
            smallCaretDownButtonStyles,
            externalCaretDownButtonStyles
          ),
        };
      default:
        return {
          styles: externalStyles,
          iconStyles: externalIconStyles,
          optionStyles: externalOptionStyles,
          caretDownButtonStyles: externalCaretDownButtonStyles,
        };
    }
  }
);

export const getComboBoxSizedStyles = (
  size: ActionControlSize,
  props?: Partial<AppComboBoxInternalProps>
): ComboBoxSizedStyles =>
  getComboBoxSizedStylesInternal(
    size,
    props ? props.pinToPreviousComboBox : undefined,
    props ? props.showLabel : undefined,
    props ? props.styles : undefined,
    props ? props.optionStyles : undefined,
    props ? props.caretDownButtonStyles : undefined,
    props ? props.iconStyles : undefined,
    props ? props.identifiersAbove : undefined
  );
